import axios from 'axios'
import { throttle } from 'throttle-debounce'

import getLocaleParam from 'Pods/Shared/Utils/getLocaleParam'

import queryClient from 'Utils/queryClient'

import buildSearchParams from '../buildSearchParams'
import { searchPageStore } from '../index'
import normalizeMeta from '../normalizeMeta'

const CURRENT_PATH = 'preview-search'
// Matches the listing controller's DEFAULT_PER_PAGE; omitted from params when unchanged so the
// cache key (and URL) stays clean for the common case.
export const DEFAULT_PER_PAGE = 120
// Mirrors ListingPage's react-query cache window: re-applying a seen filter combo (or paging
// back) is served from cache instead of refetching.
const STALE_TIME = 10 * 60 * 1000

// In-flight request; aborted when a newer fetch for a *different* cache key starts, so stale
// responses never overwrite results (react-query only dedupes identical keys). An identical-key
// repeat must NOT abort: react-query would dedupe the new fetchQuery onto the already-aborted
// promise, both cancellation handlers would bail out, and isLoading would stay true forever.
let abortController = null
let inFlightCacheKey = null

// Append `.json` to the current localized path so the listing controller answers in JSON,
// preserving locale and any category slugs already in the URL.
const getJsonUrl = () => {
  if (typeof window === 'undefined') {
    return `/${CURRENT_PATH}.json`
  }
  const path = window.location.pathname.replace(/\/+$/, '')
  return `${path || `/${CURRENT_PATH}`}.json`
}

// GenerateListingPageUrl returns English, locale-less paths (/preview-search?...), so pushing
// the backend URL verbatim on /es/preview-search would drop the /es prefix - and the next
// getJsonUrl() call would then fetch the default-locale endpoint. Re-apply the locale prefix
// exactly like ListingPage.jsx's applyLocalePrefix does. The preview-search path segment itself
// is not translated (no route_translator entry), so prefixing is sufficient.
const applyLocalePrefix = (url = '') => {
  const locale = getLocaleParam()
  if (!locale) {
    return url
  }

  const normalizedPath = url.startsWith('/') ? url : `/${url}`
  const alreadyPrefixed = normalizedPath === `/${locale}` ||
    normalizedPath.startsWith(`/${locale}/`) ||
    normalizedPath.startsWith(`/${locale}?`)

  return alreadyPrefixed ? normalizedPath : `/${locale}${normalizedPath}`
}

// The search term lives in the URL query (?keywords=...), not the store, so it must be re-read
// on every fetch - otherwise paging/filtering a search would drop the term and return unscoped
// results. Safe to read directly: fetchResults only ever runs client-side.
const getUrlKeywords = () => {
  if (typeof window === 'undefined') {
    return ''
  }
  return new URLSearchParams(window.location.search).get('keywords') || ''
}

// Stable cache key: `current_path` is constant, empty arrays and undefined are dropped, so
// equivalent filter combos map to the same react-query entry (mirrors ListingPage).
const getCacheKeyParams = (params) => {
  const { current_path: _currentPath, ...rest } = params
  return Object.fromEntries(
    Object.entries(rest).filter(
      ([, value]) => value !== undefined && !(Array.isArray(value) && value.length === 0)
    )
  )
}

/**
 * Fetches search results for the current store filters and writes them back to the store.
 * Resets to page 1 unless an explicit `page` is given (pagination). Safe to call repeatedly -
 * the previous request is aborted, and identical filter combos are served from the react-query
 * cache.
 *
 * @param {Object} [options]
 * @param {number} [options.page] - 1-based page; omitted/falsy resets to page 1 (so a plain
 *   perPage change from the UI restarts pagination)
 * @param {number} [options.perPage] - results per page. Defaults to the perPage from the last
 *   response so paging preserves the user's selection.
 * @param {boolean} [options.skipUrlUpdate] - do not push the canonical URL to history; used by
 *   the popstate handler, where the browser already restored the URL and a push would bury the
 *   forward entry.
 * @returns {Promise<void>}
 */
export const fetchResults = ({ page, perPage, skipUrlUpdate = false } = {}) => {
  const state = searchPageStore.getState()
  const targetPage = page || 1
  const targetPerPage = perPage || state.meta?.perPage || DEFAULT_PER_PAGE
  const params = buildSearchParams(state)
  params.current_path = CURRENT_PATH
  const keywords = getUrlKeywords()
  if (keywords) {
    params.keywords = keywords
  }
  if (targetPage > 1) {
    params.page = targetPage
  }
  if (targetPerPage !== DEFAULT_PER_PAGE) {
    params.per_page = targetPerPage
  }

  const url = getJsonUrl()
  const queryKey = ['searchPage', url, getCacheKeyParams(params)]
  const cacheKey = JSON.stringify(queryKey)

  if (abortController && cacheKey !== inFlightCacheKey) {
    abortController.abort()
    abortController = null
  }
  if (!abortController) {
    abortController = new AbortController()
  }
  inFlightCacheKey = cacheKey
  const { signal } = abortController

  searchPageStore.setState({ isLoading: true, fetchError: null, page: targetPage })

  return queryClient
    .fetchQuery({
      queryKey,
      queryFn: () => axios.get(url, { params, signal }),
      staleTime: STALE_TIME,
      // Fail fast on a search error (show the error/empty state) instead of the shared client's
      // default retries; the manual abort above already guards against stale overwrites.
      retry: false,
    })
    .then(({ data }) => {
      const {
        data: results = [],
        meta = {},
        filtersOptions = {},
        tags = [],
        url: newUrl,
      } = data || {}

      searchPageStore.setState({
        results,
        meta: normalizeMeta(meta),
        filtersOptions,
        resultTags: tags,
        isLoading: false,
      })

      if (newUrl && !skipUrlUpdate && typeof window !== 'undefined' && window.history?.pushState) {
        const targetUrl = applyLocalePrefix(newUrl)
        // Deduped identical-key fetches resolve every attached chain; only the first may push, or
        // repeats would stack duplicate history entries for the same URL.
        if (targetUrl !== window.location.pathname + window.location.search) {
          window.history.pushState({}, document.title, targetUrl)
        }
      }
    })
    .catch((error) => {
      if (axios.isCancel?.(error) || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
        return
      }
      searchPageStore.setState({ isLoading: false, fetchError: error })
    })
}

// Filter changes can fire in bursts (typing, multi-select); throttle like the legacy listing page.
export const throttledFetchResults = throttle(1000, fetchResults)
